/**
 * GeneratedDocViewer (P3-C) — Slide-in side panel hiển thị markdown doc.
 */
import { Copy, X } from "lucide-react";
import * as React from "react";
import ReactMarkdown from "react-markdown";

import { externalSystemsApi } from "@/api/externalSystems";
import { Button } from "@/components/ui/button";
import { useUIStore } from "@/stores/ui";

interface Props {
  systemId: string | null;
  onClose: () => void;
}

export function GeneratedDocViewer({ systemId, onClose }: Props): JSX.Element | null {
  const pushToast = useUIStore((s) => s.pushToast);
  const [doc, setDoc] = React.useState<string | null>(null);
  const [name, setName] = React.useState<string>("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!systemId) return;
    let cancelled = false;
    setLoading(true);
    setDoc(null);
    void (async () => {
      try {
        const detail = await externalSystemsApi.get(systemId);
        if (cancelled) return;
        setName(detail.name);
        setDoc(detail.generated_doc ?? "_(chưa có doc — sync để gen)_");
      } catch (e) {
        if (cancelled) return;
        pushToast({
          variant: "destructive",
          title: "Không tải được doc",
          description: e instanceof Error ? e.message : String(e),
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [systemId, pushToast]);

  // ESC to close
  React.useEffect(() => {
    if (!systemId) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [systemId, onClose]);

  const copyToClipboard = async (): Promise<void> => {
    if (!doc) return;
    try {
      await navigator.clipboard.writeText(doc);
      pushToast({ variant: "success", title: "Đã copy doc vào clipboard" });
    } catch {
      pushToast({ variant: "destructive", title: "Copy thất bại" });
    }
  };

  if (!systemId) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[55] bg-black/30 backdrop-blur-[2px] animate-fade-in"
        onClick={onClose}
        aria-hidden
      />
      {/* Side panel */}
      <aside
        className="fixed right-0 top-0 bottom-0 z-[56] w-full sm:w-[600px] bg-background border-l shadow-2xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
        aria-label="Generated documentation"
      >
        <header className="flex items-center justify-between px-4 py-3 border-b bg-gradient-mesh shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold truncate">{name || "Doc"}</h2>
            <p className="text-[10px] text-muted-foreground">
              AI-generated documentation
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {doc ? (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => void copyToClipboard()}
                title="Copy markdown"
              >
                <Copy className="size-4" />
              </Button>
            ) : null}
            <Button
              size="icon"
              variant="ghost"
              onClick={onClose}
              title="Đóng (Esc)"
            >
              <X className="size-4" />
            </Button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-6 bg-muted rounded w-2/3" />
              <div className="h-3 bg-muted rounded" />
              <div className="h-3 bg-muted rounded w-5/6" />
              <div className="h-3 bg-muted rounded w-4/6" />
            </div>
          ) : doc ? (
            <article className="prose prose-sm dark:prose-invert max-w-none prose-headings:font-semibold prose-h1:text-xl prose-h2:text-base prose-h3:text-sm prose-code:text-xs prose-code:bg-muted prose-code:px-1 prose-code:rounded prose-pre:text-xs prose-pre:bg-muted prose-table:text-xs">
              <ReactMarkdown>{doc}</ReactMarkdown>
            </article>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              Chưa có nội dung. Click "Sync" để gen doc.
            </p>
          )}
        </div>
      </aside>
    </>
  );
}
